import React from 'react';
import { ApplicationStatus, UIApplicationCardState, UIExtendedApplication } from 'types';

import moment from 'moment';
import { HiClock } from 'react-icons/hi';
interface Props {
	application: UIExtendedApplication;
	applicationCardState: UIApplicationCardState;
	showRounds?: boolean;
}
// pass children
const StatusContainer = ({ children }: { children: React.ReactNode }) => {
	return <div className="flex md:grid gap-[16px] items-center">{children}</div>;
};

const CurrentApplicationStatus = ({ application, applicationCardState, showRounds = true }: Props) => {
	const expirtDateText = moment(application?.expiryDate).format('DD MMM, hh:mm A');
	const applicationRoundsIds = application.applicationRounds || [];
	const currentRoundNumber = application.ongoingRound?.order || 0;

	const getStatusUI = () => {
		if (application.pendingForAdminApproval || application.adminRejectedOfferLetter) {
			return (
				<div className="w-fit dark:bg-[#374151] px-[10px] py-[2px] rounded-[6px] border-[1px] border-[#FACA15]">
					<p className=" text-[#FACA15] font-inter text-[12px] font-[500] leading-[18px]">Pending</p>
				</div>
			);
		} else if (applicationCardState === UIApplicationCardState.MATCHED_CARD) {
			return (
				<>
					{application?.expiryDate && (
						<div className="flex gap-[4px] flex-wrap md:flex-col md:items-end">
							<p className="text-gray-500 dark:text-[#C7C9D9] font-inter text-[12px] font-[400] leading-[16px]">
								Expires at
							</p>
							<div className="flex items-center gap-[4px]">
								<HiClock className="text-[#E57A00] dark:text-[#FDED72] h-[16px] w-[16px]" />
								<p className="text-[#E57A00] dark:text-[#FDED72] font-inter text-[12px] font-[400] leading-[16px]">
									{expirtDateText}
								</p>
							</div>
						</div>
					)}
				</>
			);
		} else if (
			[UIApplicationCardState.APPLIED_CARD, UIApplicationCardState.PENDING_FOR_OPT_OUT_APPROVAL_CARD].includes(
				applicationCardState,
			) ||
			([UIApplicationCardState.CHANGED_OFFER_TERMS_CARD].includes(applicationCardState) &&
				!application.history?.some(history => history.newStatus === ApplicationStatus.OFFER_ROLLED_OUT))
		) {
			return (
				<div className="w-fit dark:bg-[#374151] px-[10px] py-[2px] rounded-[6px] border-[1px] border-[#FACA15]">
					<p className=" text-[#FACA15] font-inter text-[12px] font-[500] leading-[18px]">Pending</p>
				</div>
			);
		} else if (
			[
				UIApplicationCardState.WAITING_FOR_RECRUITER_ACTION_CARD,
				UIApplicationCardState.PENDING_FOR_ASSIGNMENT_SUBMISSION_APPROVAL_CARD,
			].includes(applicationCardState)
		) {
			return (
				<StatusContainer>
					<>
						<div className="w-fit dark:bg-[#374151] px-[10px] py-[2px] rounded-[6px] border-[1px] border-[#FACA15]">
							<p className=" text-[#FACA15] font-inter text-[12px] font-[500] leading-[18px]">Pending</p>
						</div>
						<div className="flex items-center gap-[8px]">
							{applicationRoundsIds.map((round, i) => {
								return (
									<div
										className={`${currentRoundNumber === i + 1
												? 'bg-[#FFCC00]'
												: currentRoundNumber < i + 1
													? 'bg-[#C7C9D9]'
													: 'bg-[#06C270]'
											} w-[28px] h-[8px] rounded-[8px]`}
									></div>
								);
							})}
						</div>
					</>
				</StatusContainer>
			);
		} else if (
			[
				UIApplicationCardState.QUALIFIED_ALL_ROUNDS_CARD,
				UIApplicationCardState.PENDING_FOR_OFFER_LETTER_APPROVAL_CARD,
			].includes(applicationCardState)
		) {
			return (
				<StatusContainer>
					<>
						<div className="w-fit dark:bg-[#374151] px-[10px] py-[2px] rounded-[6px] border-[1px] border-[#FACA15]">
							<p className=" text-[#FACA15] font-inter text-[12px] font-[500] leading-[18px]">Pending</p>
						</div>
						{showRounds && applicationRoundsIds.length > 0 && (
							<div className="flex items-center gap-[8px]">
								{applicationRoundsIds.map((round, i) => {
									return <div className={`bg-[#06C270] w-[28px] h-[8px] rounded-[8px]`}></div>;
								})}
							</div>
						)}
					</>
				</StatusContainer>
			);
		} else if (applicationCardState === UIApplicationCardState.SHORTLISTED_CARD) {
			return (
				<div className="w-fit dark:bg-[#374151] px-[10px] py-[2px] rounded-[6px] border-[1px] border-[#31C48D]">
					<p className=" text-[#31C48D] font-inter text-[12px] font-[500] leading-[18px]">Shortlisted</p>
				</div>
			);
		} else if (applicationCardState === UIApplicationCardState.CLOSED_CARD) {
			const showExpiredBadge = [
				ApplicationStatus.EXPIRED,
				ApplicationStatus.CLOSED_BY_ADMIN,
				ApplicationStatus.CLOSED_BY_RECRUITER,
			].includes(application.status as ApplicationStatus);
			const closedLabel = showExpiredBadge ? 'Expired' : 'Closed';
			return (
				<div className="w-fit dark:bg-[#374151] px-[10px] py-[2px] rounded-[6px] border-[1px] border-[#F98080]">
					<p className=" text-[#F98080] font-inter text-[12px] font-[500] leading-[18px]">{closedLabel}</p>
				</div>
			);
		} else if (applicationCardState === UIApplicationCardState.REJECTED_CARD) {
			return (
				<div className="w-fit dark:bg-[#374151] px-[10px] py-[2px] rounded-[6px] border-[1px] border-[#F98080]">
					<p className=" text-[#F98080] font-inter text-[12px] font-[500] leading-[18px]">Not Cleared</p>
				</div>
			);
		} else if (applicationCardState === UIApplicationCardState.PLACED_CARD) {
			return (
				<div className="w-fit dark:bg-[#374151] px-[10px] py-[2px] rounded-[6px] border-[1px] border-[#31C48D]">
					<p className=" text-[#31C48D] font-inter text-[12px] font-[500] leading-[18px]">LevelUp</p>
				</div>
			);
		} else if (applicationCardState === UIApplicationCardState.JOBSEEKER_OPTED_OUT_CARD) {
			return (
				<div className="w-fit dark:bg-[#374151] px-[10px] py-[2px] rounded-[6px] border-[1px] border-[#AC94FA]">
					<p className=" text-[#AC94FA] font-inter text-[12px] font-[500] leading-[18px]">Not Interested</p>
				</div>
			);
		} else if (
			[UIApplicationCardState.OFFERED_CARD].includes(applicationCardState) ||
			(UIApplicationCardState.CHANGED_OFFER_TERMS_CARD &&
				application.history?.some(history => history.newStatus === ApplicationStatus.OFFER_ROLLED_OUT))
		) {
			return (
				<div className="w-fit dark:bg-[#374151] px-[10px] py-[2px] rounded-[6px] border-[1px] border-[#31C48D]">
					<p className=" text-[#31C48D] font-inter text-[12px] font-[500] leading-[18px]">Cleared</p>
				</div>
			);
		} else if (
			[
				UIApplicationCardState.ASSIGNMENT_ROUND_IN_PROGRESS_CARD,
				UIApplicationCardState.INTERVIEW_ROUND_IN_PROGRESS_CARD,
			].includes(applicationCardState)
		) {
			return (
				<StatusContainer>
					<>
						<div className="w-fit dark:bg-[#374151] px-[10px] py-[2px] rounded-[6px] border-[1px] border-[#31C48D]">
							<p className=" text-[#31C48D] font-inter text-[12px] font-[500] leading-[18px]">
								Round {currentRoundNumber}
							</p>
						</div>
						{showRounds && (
							<div className="flex items-center gap-[8px]">
								{applicationRoundsIds.map((round, i) => {
									return (
										<div
											className={`${currentRoundNumber === i + 1
													? 'bg-[#FFCC00]'
													: currentRoundNumber < i + 1
														? 'bg-[#C7C9D9]'
														: 'bg-[#06C270]'
												} w-[28px] h-[8px] rounded-[8px]`}
										></div>
									);
								})}
							</div>
						)}
					</>
				</StatusContainer>
			);
		}
	};
	return <div className="mt-[12px] bg-white">{getStatusUI()}</div>;
};

export default CurrentApplicationStatus;
